import { useTranslation } from 'react-i18next';
import { useMutation, QueryKey as TQueryKey, useQuery } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import {
    getBudgetVersions,
    createBudgetVersion,
    updateBudgetVersion,
    deleteBudgetVersion,
    createSnapshot,
    BudgetVersion,
} from '../../services/budgetVersion';
import { QueryKey } from '../../enums/QueryKey';
import { queryClient } from '../../services/queryClient';
import { useCreateRecord, useUpdateRecord, useDeleteRecord } from './crudOperations';

export function useBudgetVersions(): [BudgetVersion[], boolean] {
    const queryKey = [QueryKey.BudgetVersions] as TQueryKey;

    const {
        data: versions,
        isLoading,
        isFetching,
        isFetchedAfterMount,
    } = useQuery<BudgetVersion[], Error>({
        queryKey,
        queryFn: () => getBudgetVersions(),
        initialData: [],
    });

    return [versions, isFetchedAfterMount ? isLoading : isFetching];
}

export function useCreateBudgetVersion({
    onSettled,
}: Pick<UseMutationOptions<BudgetVersion, Error, Parameters<typeof createBudgetVersion>[0]>, 'onSettled'>) {
    const { t } = useTranslation('budget');
    return useCreateRecord<BudgetVersion, Error, Parameters<typeof createBudgetVersion>[0]>({
        createRecordFn: createBudgetVersion,
        fetchRecordsQueryKey: [QueryKey.BudgetVersions],
        recordName: t('entity.version'),
        onSettled,
    });
}

export function useUpdateBudgetVersion({
    onSettled,
}: Pick<UseMutationOptions<BudgetVersion, Error, Parameters<typeof updateBudgetVersion>[0]>, 'onSettled'>) {
    const { t } = useTranslation('budget');
    return useUpdateRecord<BudgetVersion, Error, Parameters<typeof updateBudgetVersion>[0]>({
        updateRecordFn: updateBudgetVersion,
        fetchRecordsQueryKey: [QueryKey.BudgetVersions],
        recordName: t('entity.version'),
        onSettled,
    });
}

export function useDeleteBudgetVersion({ onSettled }: Pick<UseMutationOptions<unknown, Error, number>, 'onSettled'>) {
    const { t } = useTranslation('budget');
    return useDeleteRecord<unknown, Error, number>({
        deleteRecordFn: deleteBudgetVersion,
        fetchRecordsQueryKey: [QueryKey.BudgetVersions],
        recordName: t('entity.version'),
        onSuccess: (_, deletedId) => {
            queryClient.setQueryData<BudgetVersion[]>([QueryKey.BudgetVersions], (prev) =>
                (prev || []).filter((version) => version.id !== deletedId)
            );
        },
        onSettled,
    });
}

export function useCreateSnapshot({
    onSettled,
}: Pick<UseMutationOptions<BudgetVersion, Error, number>, 'onSettled'> = {}) {
    return useMutation<BudgetVersion, Error, number>({
        mutationFn: createSnapshot,
        onSuccess: (snapshotVersion) => {
            queryClient.setQueryData<BudgetVersion[]>([QueryKey.BudgetVersions], (prev) =>
                (prev || []).map((version) => (version.id === snapshotVersion.id ? snapshotVersion : version))
            );
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetVersions] });
        },
        onSettled,
    });
}
